import baseAPI from "@/api/base";
import {
  LoginCredentals,
  LoginOAuthCredentals,
  SignupCredentals,
  SignupOAuthCredentals,
  VerifyMfaPayload,
} from "@/dtos/Auth.dto";
import {
  EDIT_USER_URL,
  FORGOT_PASSWORD_URL,
  LINK_SOCIAL_URL,
  LINKED_ACCOUNTS_URL,
  LOGIN_OAUTH_URL,
  LOGIN_URL,
  LOGOUT_URL,
  MFA_DISABLE_URL,
  MFA_EMAIL_SEND_URL,
  MFA_EMAIL_SETUP_URL,
  MFA_EMAIL_VERIFY_SETUP_URL,
  MFA_STATUS_URL,
  MFA_TOTP_SETUP_URL,
  MFA_TOTP_VERIFY_SETUP_URL,
  MFA_VERIFY_URL,
  REFRESH_TOKEN_URL,
  REQUEST_EMAIL_VERIFICATION,
  REQUEST_SET_PASSWORD_AND_VERIFY_URL,
  RESET_PASSWORD,
  RESET_PASSWORD_AUTHED_URL,
  SET_PASSWORD_AND_VERIFY_URL,
  SIGNUP_OAUTH_URL,
  SIGNUP_URL,
  UNLINK_SOCIAL_URL,
  SET_PASSWORD_URL,
  VERIFY_EMAIL_URL,
} from "@/utils/urls";
import { AxiosResponse } from "axios";
import {
  EditUser,
  ForgotPassword,
  GetUser,
  ResetPassword,
  ResetPasswordObj,
  SetPasswordPasswordAndVerifyObj,
} from "@/dtos/User.dto";

class Auth {
  static login = async (loginCredentals: LoginCredentals): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(LOGIN_URL, {
      ...loginCredentals,
    });
    return response;
  };

  // Goes through baseAPI deliberately — a plain call from user-facing code
  // (e.g. a "log out" button), not the interceptor's internal refresh path,
  // which bypasses baseAPI to avoid recursing back through the response
  // interceptor that this very call would otherwise trigger.
  static refreshAccessToken = async (refreshToken: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(REFRESH_TOKEN_URL, { refreshToken });
    return response;
  };

  static logout = async (refreshToken?: string | null): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(LOGOUT_URL, { refreshToken });
    return response;
  };

  static loginOAuth = async (
    loginOAuthCredentals: LoginOAuthCredentals
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(LOGIN_OAUTH_URL, {
      ...loginOAuthCredentals,
    });
    return response;
  };

  static signup = async (registrationCredentals: SignupCredentals): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(SIGNUP_URL, {
      ...registrationCredentals,
    });
    return response;
  };

  static signupOAuth = async (
    registrationOAuthCredentals: SignupOAuthCredentals
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(SIGNUP_OAUTH_URL, {
      ...registrationOAuthCredentals,
    });
    return response;
  };

  static forgotPassword = async (forgotPassword: ForgotPassword): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(FORGOT_PASSWORD_URL, {
      ...forgotPassword,
    });
    return response;
  };

  static resetPassword = async (resetPassword: ResetPasswordObj): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(`${RESET_PASSWORD}/${resetPassword.token}`, {
      ...resetPassword.resetPasswordObj,
    });
    return response;
  };

  static resetPasswordAuthed = async (resetPassword: ResetPassword): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(RESET_PASSWORD_AUTHED_URL, {
      ...resetPassword,
    });
    return response;
  };

  static setPasswordAndVerify = async (
    setPasswordPasswordAndVerifyObj: SetPasswordPasswordAndVerifyObj
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(
      `${SET_PASSWORD_AND_VERIFY_URL}/${setPasswordPasswordAndVerifyObj.token}`,
      {
        ...setPasswordPasswordAndVerifyObj.setPasswordObj,
      }
    );
    return response;
  };

  static requestSetPasswordAndVerify = async (getUser: GetUser): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(REQUEST_SET_PASSWORD_AND_VERIFY_URL, {
      params: getUser,
    });
    return response;
  };

  static requestEmailVerification = async (getUser: GetUser): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(REQUEST_EMAIL_VERIFICATION, {
      params: getUser,
    });
    return response;
  };

  static verifyEmail = async (userId: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(`${VERIFY_EMAIL_URL}/${userId}`);
    return response;
  };

  static linkSocial = async (data: {
    provider: "google" | "outlook";
    accessToken: string;
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(LINK_SOCIAL_URL, data);
    return response;
  };

  static unlinkSocial = async (data: {
    provider: "google" | "outlook";
  }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(UNLINK_SOCIAL_URL, data);
    return response;
  };

  static getLinkedAccounts = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(LINKED_ACCOUNTS_URL);
    return response;
  };

  static setPassword = async (data: { newPassword: string }): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(SET_PASSWORD_URL, data);
    return response;
  };

  static updateAccount = async (formData: FormData): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.put(EDIT_USER_URL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  };

  static setupTotp = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(MFA_TOTP_SETUP_URL);
    return response;
  };

  static verifySetupTotp = async (code: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(MFA_TOTP_VERIFY_SETUP_URL, { code });
    return response;
  };

  static setupEmailMfa = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(MFA_EMAIL_SETUP_URL);
    return response;
  };

  static verifySetupEmailMfa = async (code: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(MFA_EMAIL_VERIFY_SETUP_URL, { code });
    return response;
  };

  // Same step-up token requirement as verifyMfa — no real session exists yet.
  static sendEmailLoginCode = async (pendingToken: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(
      MFA_EMAIL_SEND_URL,
      {},
      { headers: { Authorization: `Bearer ${pendingToken}` } }
    );
    return response;
  };

  static getMfaStatus = async (): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.get(MFA_STATUS_URL);
    return response;
  };

  static disableMfa = async (password: string): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(MFA_DISABLE_URL, { password });
    return response;
  };

  // The step-up token proves the password step passed but no session exists yet,
  // so it must be sent explicitly — the request interceptor only attaches the
  // real sessionToken, which is still null at this point in the login flow.
  static verifyMfa = async (
    pendingToken: string,
    payload: VerifyMfaPayload
  ): Promise<AxiosResponse> => {
    const response: AxiosResponse = await baseAPI.post(MFA_VERIFY_URL, payload, {
      headers: { Authorization: `Bearer ${pendingToken}` },
    });
    return response;
  };
}

export default Auth;
